import { useCallback, useEffect, useState } from "react";
import * as FC from "@fluentui/react-components";
import * as IC from "@fluentui/react-icons";
import { useNavigate } from "react-router-dom";
import { apiClient } from "../../services/api";
import { toDateTimeString } from "../../date_utils";

const useStyles = FC.makeStyles({
    root: {
        display: "flex",
        flexDirection: "column",
        ...FC.shorthands.gap("12px"),
        ...FC.shorthands.padding("16px"),
        minWidth: 0,
    },
    controls: {
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        ...FC.shorthands.gap("10px"),
    },
    tableWrap: { overflowX: "auto" },
    table: {
        width: "100%",
        borderCollapse: "collapse",
        "& th": {
            textAlign: "left",
            whiteSpace: "nowrap",
            ...FC.shorthands.padding("8px"),
            borderBottom: `1px solid ${FC.tokens.colorNeutralStroke1}`,
        },
        "& td": {
            ...FC.shorthands.padding("8px"),
            borderBottom: `1px solid ${FC.tokens.colorNeutralStroke2}`,
            verticalAlign: "top",
        },
    },
    muted: { color: FC.tokens.colorNeutralForeground3 },
    error: { color: FC.tokens.colorPaletteRedForeground1 },
});

const typeOptions = [
    ["", "全部账号"],
    ["internal", "人员账号"],
    ["external", "客户账号"],
];

/**
 * Platform-wide account search. Read-only by construction: there is no create, edit, disable or
 * re-bind control anywhere on this page, and the only action per row navigates into that account's
 * own tenant workbench, where the tenant is fixed by the URL. Cross-tenant search is useful for
 * operations; cross-tenant WRITING is what the tenant-scoped entry points exist to prevent.
 */
export const AccountIndex = () => {
    const styles = useStyles();
    const navigate = useNavigate();
    const [search, setSearch] = useState("");
    const [userType, setUserType] = useState("");
    const [tenantId, setTenantId] = useState("");
    const [tenants, setTenants] = useState([]);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        apiClient
            .getTenants({ status: "active" })
            .then((items) => setTenants(items || []))
            .catch((loadError) => console.error("Failed to load tenants:", loadError));
    }, []);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await apiClient.getAccountIndex({
                search: search.trim() || undefined,
                userType: userType || undefined,
                tenantId: tenantId || undefined,
            });
            setResult(response);
        } catch (loadError) {
            console.error("Failed to search accounts:", loadError);
            setError(loadError.message || "查询失败");
            setResult(null);
        } finally {
            setLoading(false);
        }
    }, [search, tenantId, userType]);

    useEffect(() => {
        load();
        // Only on mount: subsequent searches are explicit, so a half-typed term does not fan out
        // one cross-tenant query per keystroke.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const items = result?.items || [];

    return (
        <div className={styles.root}>
            <FC.Title3>账号检索（只读）</FC.Title3>
            <FC.Caption1 className={styles.muted}>
                本页只用于查找账号。任何创建、修改、停用都必须进入对应租户的工作台。
            </FC.Caption1>
            <div className={styles.controls}>
                <FC.Input
                    style={{ minWidth: 260 }}
                    placeholder="账号、姓名、公司、客户编号、邮箱"
                    value={search}
                    onChange={(_, data) => setSearch(data.value)}
                    onKeyDown={(event) => event.key === "Enter" && load()}
                />
                <FC.Dropdown
                    style={{ minWidth: 140 }}
                    selectedOptions={[userType]}
                    value={typeOptions.find(([value]) => value === userType)?.[1] || "全部账号"}
                    onOptionSelect={(_, data) => setUserType(data.optionValue || "")}
                >
                    {typeOptions.map(([value, label]) => (
                        <FC.Option key={value || "all"} value={value}>
                            {label}
                        </FC.Option>
                    ))}
                </FC.Dropdown>
                <FC.Dropdown
                    style={{ minWidth: 220 }}
                    selectedOptions={tenantId ? [tenantId] : [""]}
                    value={
                        tenants.find((tenant) => tenant.id?.toString() === tenantId)?.name || "全部租户"
                    }
                    onOptionSelect={(_, data) => setTenantId(data.optionValue || "")}
                >
                    <FC.Option value="">全部租户</FC.Option>
                    {tenants.map((tenant) => (
                        <FC.Option key={tenant.id} value={tenant.id?.toString()}>
                            {`${tenant.seq} - ${tenant.name}`}
                        </FC.Option>
                    ))}
                </FC.Dropdown>
                <FC.Button appearance="primary" icon={<IC.SearchRegular />} onClick={load} disabled={loading}>
                    检索
                </FC.Button>
            </div>

            {error && <div className={styles.error}>{error}</div>}
            {result?.truncated && (
                <FC.Caption1 className={styles.muted}>
                    共 {result.total} 条，已截断显示前 {result.limit} 条，请细化检索条件。
                </FC.Caption1>
            )}

            <div className={styles.tableWrap}>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th>类型</th>
                            <th>账号</th>
                            <th>姓名/联系人</th>
                            <th>租户</th>
                            <th>业主</th>
                            <th>公司 / 部门</th>
                            <th>状态</th>
                            <th>最近登录</th>
                            <th>操作</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.length === 0 && (
                            <tr>
                                <td colSpan={9} className={styles.muted}>
                                    {loading ? "查询中…" : "没有匹配的账号"}
                                </td>
                            </tr>
                        )}
                        {items.map((item) => (
                            <tr key={`${item.userType}-${item.id}`}>
                                <td>{item.userType === "internal" ? "人员" : "客户"}</td>
                                <td>
                                    {item.account}
                                    {item.isPlatformAdmin && (
                                        <FC.Badge appearance="tint" color="brand" size="small">
                                            平台管理员
                                        </FC.Badge>
                                    )}
                                </td>
                                <td>{item.name}</td>
                                <td>{item.tenantSeq ? `${item.tenantSeq} · ${item.tenantName}` : "-"}</td>
                                <td>{item.assetOwnerName || "-"}</td>
                                <td>{item.companyName || item.department || "-"}</td>
                                <td>{item.disabled ? "已停用" : "正常"}</td>
                                <td>{item.lastLoginAt ? toDateTimeString(item.lastLoginAt) : "-"}</td>
                                <td>
                                    <FC.Button
                                        size="small"
                                        appearance="subtle"
                                        disabled={!item.tenantId}
                                        onClick={() =>
                                            navigate(`/system/tenants/${item.tenantId}/manage`)
                                        }
                                    >
                                        前往该租户工作台
                                    </FC.Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default AccountIndex;
